import React, { useContext } from 'react';
import { CloudDownloadOutlined, CloudUploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, message, Modal, Space, Tooltip, Upload } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../../repository/context/userContext';
import { btnActive } from '../../../../styles';
import { GRAPHICS } from '../../../../constants/define/translation';
import { toImageUrl, uploadFileAssetsToAzureBlob } from '../../../../utils/azure';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { MESSAGE_ERROR } from '../../../../constants/message';

interface ActionsColumnProps {
  record: any;
  onRefresh?: (loading?: boolean) => void;
  onLoading: (loading: boolean) => void;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(GRAPHICS);
  const { record, onRefresh, onLoading } = props;
  const userContext = useContext(UserContext);

  const onHandleUpload = async (options: any) => {
    Modal.confirm({
      title: t('popup.title'),
      okText: t('popup.okBtn'),
      cancelText: t('popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('popup.uploadImage')}
          isConfirmPassword
          textConfirmPassword={t('popup.confirmPassword')}
        />
      ),
      onOk() {
        onLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const { onSuccess, onError, file } = options;
        const onFunc = () => {
          uploadFileAssetsToAzureBlob({
            blob: file,
            fileType: file.type,
            path: record?.link,
          })
            .then(() => {
              onSuccess(t('message.uploadImageSuccess'));
              message.success(t('message.uploadImageSuccess'));
              onRefresh && onRefresh(true);
              onLoading(false);
            })
            .catch(error => {
              onError({ error });
              onLoading(false);
            });
        };

        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
          onLoading(false);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              onFunc();
            })
            .catch((e: any) => {
              onLoading(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  const onHandleDownload = () => {
    fetch(toImageUrl(record?.link))
      .then(resp => resp.blob())
      .then(blob => {
        message.success(t('message.downloadImageSuccess'));
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = record?.link.substring(record?.link?.lastIndexOf('/') + 1, record?.link?.length);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => MESSAGE_ERROR.M02);
  };

  return (
    <StyledContainer>
      <Space size="small">
        <Tooltip title={t('table.action.download')}>
          <Button icon={<CloudDownloadOutlined />} onClick={onHandleDownload} />
        </Tooltip>
        <Tooltip title={t('table.action.upload')}>
          <Upload listType="picture" maxCount={1} showUploadList={false} customRequest={onHandleUpload}>
            <Button icon={<CloudUploadOutlined />} className="active" />
          </Upload>
        </Tooltip>
        {/* <Tooltip title={t('table.action.revert')}>
          <Upload listType="picture" maxCount={1}>
            <Button icon={<CloudUploadOutlined />} className="active" />
          </Upload>
        </Tooltip> */}
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.5rem;
  ${btnActive}
  button {
    cursor: pointer;
  }
`;
